/**
 * `prototype/design-tokens.json` as a typed theme (D78 as-is — P6 Owns §, PRD §1).
 *
 * `prototype/design-tokens.json` stays the single visual source of truth (§7.1); this module
 * imports it rather than forking a copy, so a token edit made against the prototype is the
 * same edit the shell renders. Do not hand-maintain a second token file.
 */
import rawTokens from '../../../prototype/design-tokens.json'

export interface GreyScale {
  readonly '0': string
  readonly '1': string
  readonly '2': string
  readonly '3': string
  readonly '4': string
  readonly '5': string
  readonly '6': string
  readonly '7': string
}

export interface DesignTokens {
  readonly color: {
    readonly grey: GreyScale
    readonly surface: { readonly page: string; readonly panel: string; readonly frame: string }
    readonly content: { readonly primary: string; readonly muted: string; readonly inverse: string }
    readonly border: { readonly default: string }
    /** The Home bar's own ink — wordmark, hamburger, view control. Distinct from `content.primary`:
     *  that one is body text, and reads warm/near-white per scheme; the bar wants a neutral charcoal
     *  that holds its hue across both. */
    readonly appBar: { readonly text: string }
    /** Direction 05's Gate Detail accent (P7's `.gate-state-tile.accent`/`.gate-mode-segment.selected`). */
    readonly brand: { readonly rust: string; readonly rustSurface: string; readonly miami?: string }
    readonly signal: {
      readonly success: string
      /**
       * The green a **filled** control paints, as distinct from the green small marks draw *in*
       * (D185). One value in the light scheme and a deeper one on graphite: a 60px button and a
       * 9px LED are not the same effect at the same lightness, the same way `@keyframes pulse` is
       * not (implementation-notes.md → P7). Consumers: `.gate-open-action` and
       * `.install-test-open` only — everything else keeps `--green`.
       *
       * **Optional, and the fallback is the point.** `color` merges by *group*, so a theme that
       * restates `signal` and omits this one has no solid green — and the seven prototype-only
       * themes all predate it. Falling back to `success` gives them exactly the relationship the
       * light scheme has (one green, two jobs), rather than obliging every theme to carry a value
       * only `graphite` renders. `prototype/app.js`'s map reads it the same way; the two readers
       * disagreeing about this JSON is the defect that shows up as a colour nobody chose.
       */
      readonly successSolid?: string
      readonly pending: string
      readonly danger: string
      readonly stale: string
    }
    readonly feedback: {
      readonly successSurface: string
      readonly dangerSurface: string
      readonly warningSurface: string
      readonly warningText: string
      /** The one informational (never alarming) pair — P7's install notices. */
      readonly infoText: string
      readonly infoSurface: string
      readonly dangerText: string
    }
    readonly gate: {
      readonly barrier: string
      readonly barrierSurface: string
      readonly door: string
      readonly doorSurface: string
      readonly parking: string
      readonly parkingSurface: string
      readonly garage: string
      readonly garageSurface: string
    }
    readonly personalization: Readonly<Record<string, string>>
  }
  readonly font: {
    readonly family: { readonly ui: string; readonly mono: string }
    readonly size: Readonly<Record<string, number>>
  }
  readonly space: Readonly<Record<string, number>>
  readonly radius: Readonly<Record<string, number>>
  readonly size: {
    readonly phoneWidth: number
    readonly phoneHeight: number
    readonly statusBarHeight: number
    readonly iconChip: number
    readonly actuator: number
    readonly touchTargetMin: number
    readonly controlHeight: number
    readonly controlHeightProminent: number
  }
  readonly motion: {
    readonly fast: number
    readonly standard: number
    readonly openSimulation: number
    readonly nudgeFlight: number
  }
  readonly themes: Readonly<Record<string, ThemeOverride>>
}

/**
 * One entry of `prototype/design-tokens.json`'s `themes` map. **A theme is an override, not a
 * second token file** — the same rule the header states, one level down.
 *
 * The merge is one level deep and `color` merges **by group**: naming `signal` restates all four
 * signal colours, and a theme that names only `success` loses the other three. `radius`, `size`,
 * `space` and `font.size` merge by key instead. That asymmetry is not tidy, and it is not ours to
 * fix here — `mergeTheme()` below is a port of `prototype/app.js`'s `mergedTokens()`, and the two
 * reading the same JSON differently is a defect that would show up as a colour nobody chose.
 */
export interface ThemeOverride {
  readonly color?: Partial<DesignTokens['color']>
  readonly font?: { readonly size?: Readonly<Record<string, number>> }
  readonly radius?: Readonly<Record<string, number>>
  readonly size?: Partial<DesignTokens['size']>
  readonly space?: Readonly<Record<string, number>>
  readonly motion?: Partial<DesignTokens['motion']>
}

export const tokens: DesignTokens = rawTokens as DesignTokens

/**
 * The UI scale refresh's one knob (`ui-scale-refresh-handoff.md` A1) — baked into the
 * baseline, no setting, no persistence. Distinct from Home's layout pill, whose large position
 * still means *tile size* (A4) — and which no longer carries the `a-large-small` glyph, that
 * having been a density control of its own before the pair became one rotating button.
 */
export const UI_SCALE = 1.27

/**
 * The CSS custom-property names `global.css` reads. Kept alongside the token type so a
 * renamed token and its variable never drift apart silently.
 */
export function cssVariables(value: DesignTokens): Readonly<Record<string, string>> {
  const vars: Record<string, string> = {
    '--fs': String(UI_SCALE),
    '--g0': value.color.grey['0'],
    '--g1': value.color.grey['1'],
    '--g2': value.color.grey['2'],
    '--g3': value.color.grey['3'],
    '--g4': value.color.grey['4'],
    '--g5': value.color.grey['5'],
    '--g6': value.color.grey['6'],
    '--g7': value.color.grey['7'],
    '--green': value.color.signal.success,
    '--green-solid': value.color.signal.successSolid ?? value.color.signal.success,
    '--amber': value.color.signal.pending,
    '--red': value.color.signal.danger,
    '--stale': value.color.signal.stale,
    '--page-bg': value.color.surface.page,
    '--panel': value.color.surface.panel,
    '--frame': value.color.surface.frame,
    '--text': value.color.content.primary,
    '--muted': value.color.content.muted,
    '--inverse': value.color.content.inverse,
    '--border': value.color.border.default,
    '--accent': value.color.brand.rust,
    '--accent-surface': value.color.brand.rustSurface,
    '--success-surface': value.color.feedback.successSurface,
    '--danger-surface': value.color.feedback.dangerSurface,
    '--warning-surface': value.color.feedback.warningSurface,
    '--warning-text': value.color.feedback.warningText,
    '--info-text': value.color.feedback.infoText,
    '--info-surface': value.color.feedback.infoSurface,
    '--danger-text': value.color.feedback.dangerText,
    '--acc-barrier': value.color.gate.barrier,
    '--acc-barrier-bg': value.color.gate.barrierSurface,
    '--acc-door': value.color.gate.door,
    '--acc-door-bg': value.color.gate.doorSurface,
    '--acc-parking': value.color.gate.parking,
    '--acc-parking-bg': value.color.gate.parkingSurface,
    '--acc-car': value.color.gate.garage,
    '--acc-car-bg': value.color.gate.garageSurface,
    /**
     * D185's Miami blue — chrome, not a per-gate choice, and D185 pins it to Porsche Miamiblau
     * `#00a9ce` **verbatim** from the specimen sheet that proposed it. **D189 narrowed what it
     * paints**: the wordmark, hamburger and view control moved to `--charcoal` below, leaving the
     * bar's washed edge line as its one consumer — and **that consumer is gone too**. The line now
     * takes `--wordmark-accent`, the ring "o"'s own blue, because the bar's title became a drawn
     * mark and two near blues four pixels apart read as a mismatch rather than as two ideas
     * (`global.css`, `.app-bar::after`).
     *
     * **So this token is declared and unconsumed, deliberately.** It stays because `:root` is
     * asserted equal to `cssVariables(tokens)` and because D185 is a decision about the palette
     * rather than about one line; deleting it is a token change with a `D-x` behind it, not
     * cleanup. Nothing here should be read as "the wash is Miami" any more.
     *
     * **It reads `brand`, not `personalization`, and that split is the whole point.** It used to
     * read `personalization.miami` back when one ramp served both schemes and that entry *was*
     * the brand hex. Forking the ramp per scheme broke the coincidence: the swatch moved to a
     * legible `#1785a2`/`#46a9c6` and this variable would have moved with it. D189 shrank the
     * blast radius — it was the wordmark before, it is an edge treatment now — but not the
     * defect: a chrome token and a picker swatch have different jobs, and only one of them is
     * allowed to be chosen for contrast.
     *
     * **`brand.miami` is restated in `themes.graphite` with the same value on purpose.** `color`
     * merges by *group*, so a graphite `brand` that named only `rust`/`rustSurface` would drop
     * `miami`, fall through to the fallbacks below, and put `--miami` back in the dark delta —
     * the exact fork this repairs. The seven prototype-only themes do not restate it and do not
     * need to: nothing in `prototype/` consumes `--miami`.
     *
     * Both fallbacks are kept and neither is a literal `#00a9ce`, so a hex typed here can never
     * drift from the JSON.
     */
    '--miami': value.color.brand.miami ?? value.color.personalization['miami'] ?? value.color.gate.barrier,
    '--charcoal': value.color.appBar.text,
    '--font-ui': value.font.family.ui,
    '--font-mono': value.font.family.mono,
    '--phone-width': `${value.size.phoneWidth}px`,
    '--phone-height': `${value.size.phoneHeight}px`,
    '--statusbar-height': `${value.size.statusBarHeight}px`,
    '--icon-chip': `calc(${value.size.iconChip}px * var(--fs))`,
    '--actuator': `calc(${value.size.actuator}px * var(--fs))`,
    '--touch-target-min': `calc(${value.size.touchTargetMin}px * var(--fs))`,
    '--control-height': `calc(${value.size.controlHeight}px * var(--fs))`,
    '--control-height-prominent': `calc(${value.size.controlHeightProminent}px * var(--fs))`,
    '--motion-fast': `${value.motion.fast}ms`,
    '--motion-standard': `${value.motion.standard}ms`,
    '--motion-open-simulation': `${value.motion.openSimulation}ms`,
    '--motion-nudge': `${value.motion.nudgeFlight}ms`,
  }
  for (const [name, token] of Object.entries(value.space)) vars[`--space-${name}`] = `${token}px`
  for (const [name, token] of Object.entries(value.radius)) vars[`--radius-${name}`] = `${token}px`
  for (const [name, token] of Object.entries(value.font.size)) {
    vars[`--font-${name}`] = `calc(${token}px * var(--fs))`
  }
  /**
   * The fifteen personalization swatches, as variables — the full-strength value and its wash.
   *
   * **Variables rather than a JS record, because the ramp is now per-scheme.** The light ramp
   * (Ceramic) and `themes.graphite`'s (Lift) are two different sets of hexes, and the three
   * screens that paint a personalized gate do it *inline*, per gate, at render time. A flat
   * `Record<name, hex>` read in a component cannot follow `[data-scheme]`: it is resolved once,
   * in JS, by a module that has no idea which scheme is stamped on `<html>` — so a resident
   * switching appearance would keep the other scheme's swatch until the next reload. CSS
   * resolves the same lookup on every repaint, with no re-render and no scheme read in JS,
   * which is exactly what D188 bought by making the activation an attribute.
   *
   * **The wash is `hex + '22'` — the literal 8-digit form, not `color-mix()` or `rgb(… / …)`.**
   * That is the ~13% tint `accentFor()` produced by string arithmetic before this existed
   * (home-layout-handoff.md: personalization is a tint behind a full-strength glyph, never a
   * full-strength fill), and keeping the arithmetic byte-identical is what makes this a
   * mechanism change rather than a colour change.
   */
  for (const [name, token] of Object.entries(value.color.personalization)) {
    vars[`--pc-${name}`] = token
    vars[`--pc-${name}-wash`] = `${token}22`
  }
  return vars
}

/**
 * The one theme `web/` renders (D179): `prototype/design-tokens.json`'s `themes.graphite`.
 *
 * A named constant rather than a parameter, and `cssDarkVariables()` below takes no theme argument.
 * **D188 gave the scheme a setting and this rule is unchanged, which is the whole point of stating
 * it here.** That decision chose *whether* graphite applies — dark, auto, or light, resolved in
 * `colorScheme.ts` and stamped as `[data-scheme]` — and never *which* theme does. A signature that
 * accepts a theme name is still the toggle arriving by the back door, and the next caller to pass a
 * second name has still shipped an appearance selector nobody decided (`prototype/flows.md`'s
 * "promote one only through a `D-x`"). The preference screen has three options and none of them is
 * a theme name.
 */
export const DARK_THEME = 'graphite'

/**
 * `mergedTokens()` from `prototype/app.js`, ported. **`color` merges by group, everything else by
 * key** — see `ThemeOverride`. The two implementations read the same JSON and must not drift.
 */
function mergeTheme(base: DesignTokens, theme: string): DesignTokens {
  const override = base.themes[theme]
  if (override === undefined) return base
  const value = { ...base } as Record<string, unknown>
  for (const [group, entries] of Object.entries(override)) {
    if (group === 'color') {
      // By group: `{ ...base.color, ...entries }` replaces each named group wholesale.
      value['color'] = { ...base.color, ...(entries as object) }
    } else if (group === 'font') {
      const font = entries as { readonly size?: Readonly<Record<string, number>> }
      value['font'] = { ...base.font, ...font, size: { ...base.font.size, ...(font.size ?? {}) } }
    } else {
      value[group] = { ...(base[group as keyof DesignTokens] as object), ...(entries as object) }
    }
  }
  return value as unknown as DesignTokens
}

/**
 * The dark palette as a **delta** — only the variables whose value differs from the light set.
 *
 * `themes.graphite` overrides colour and nothing else, so a full emit would restate `--fs`, every
 * `--space-*`, every `--radius-*` and every `calc(… * var(--fs))` identically: forty-six lines
 * that can only ever go stale, in a block whose whole job is to say *what changes*. The delta is
 * also what `theme.test.tsx`'s dark-palette case asserts, so a token that stops differing has to
 * leave `global.css` rather than sit there pinning a value the JSON no longer says.
 */
export function cssDarkVariables(value: DesignTokens): Readonly<Record<string, string>> {
  const light = cssVariables(value)
  const dark = cssVariables(mergeTheme(value, DARK_THEME))
  return Object.fromEntries(Object.entries(dark).filter(([name, token]) => light[name] !== token))
}
